import { Observable } from 'rxjs';
import { CreateSequencingRunRequest } from '../../model/sequencing/create-sequencing-run-request.model';
import { CreateSequencingRunResponse } from '../../model/sequencing/create-sequencing-run-response.model';
import { SequencingRunDetail } from '../../model/sequencing/sequencing-run-detail.model';
import {
  SequencingRunListParams,
  SequencingRunListResponse
} from '../../model/sequencing/sequencing-run-list.model';
import { UpdateSequencingRunRequest } from '../../model/sequencing/update-sequencing-run-request.model';
import { UpdateSequencingRunResponse } from '../../model/sequencing/update-sequencing-run-response.model';
import {
  MarkRunAsFailedRequest,
  MarkRunAsFailedResponse
} from '../../model/sequencing/mark-run-as-failed.model';
import {
  RunSpecimen,
  MarkRunAsSuccessRequest,
  MarkRunAsSuccessResponse
} from '../../model/sequencing/mark-run-as-success.model';

/**
 * Abstract repository for Sequencing Run domain entity.
 * Defines the contract for accessing sequencing run data.
 *
 * Implementation should be provided in infrastructure layer
 * following the Dependency Inversion Principle.
 */
export abstract class SequencingRunRepository {
  /**
   * Create a new sequencing run with library assignments.
   * The run code is generated automatically by the backend.
   *
   * @param request The run parameters and library assignments
   * @returns Observable of the created run response
   */
  abstract createSequencingRun(
    request: CreateSequencingRunRequest
  ): Observable<CreateSequencingRunResponse>;

  /**
   * Get paginated list of sequencing runs with optional filters.
   * Supports filtering by status, search term, and sorting.
   *
   * @param params Query parameters for pagination, filtering, and sorting
   * @returns Observable of paginated runs response
   */
  abstract getSequencingRuns(
    params: SequencingRunListParams
  ): Observable<SequencingRunListResponse>;

  /**
   * Get a sequencing run by its ID with all library assignments.
   * Used for editing/viewing run details.
   *
   * @param runId The unique identifier of the run
   * @returns Observable of the run detail
   */
  abstract getSequencingRunById(runId: string): Observable<SequencingRunDetail>;

  /**
   * Delete a sequencing run by its ID.
   * Only runs with status CREATED can be deleted.
   * Libraries assigned to the run will be released (status changed to CREATED).
   *
   * @param runId The unique identifier of the run to delete
   * @returns Observable of the delete operation result
   */
  abstract deleteSequencingRun(runId: string): Observable<void>;

  /**
   * Update an existing sequencing run with new parameters and library assignments.
   * All existing library assignments are replaced with the new ones.
   *
   * @param runId The unique identifier of the run to update
   * @param request The updated run parameters and library assignments
   * @returns Observable of the updated run response
   */
  abstract updateSequencingRun(
    runId: string,
    request: UpdateSequencingRunRequest
  ): Observable<UpdateSequencingRunResponse>;

  /**
   * Generate and download SampleSheet for a sequencing run.
   * Available for all run statuses.
   *
   * @param runId The unique identifier of the run
   * @returns Observable of the SampleSheet file as Blob
   */
  abstract generateSampleSheet(runId: string): Observable<Blob>;

  abstract markRunAsFailed(
    request: MarkRunAsFailedRequest
  ): Observable<MarkRunAsFailedResponse>;

  abstract getRunSpecimens(runId: string): Observable<RunSpecimen[]>;

  abstract markRunAsSuccess(
    request: MarkRunAsSuccessRequest
  ): Observable<MarkRunAsSuccessResponse>;

  abstract updateRunStatus(runId: string, status: string): Observable<void>;
}
